// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SESSION_ACTIVITY_EVENT } from '../constants/sessionActivity';

const DEFAULT_IDLE_MINUTES = 30;
const CHECK_INTERVAL_MS = 30000;
const MOUSE_MOVE_THROTTLE_MS = 60000;

function resolveIdleMs() {
  const minutesRaw = import.meta.env.VITE_IDLE_SESSION_MINUTES;
  const msRaw = import.meta.env.VITE_IDLE_SESSION_MS;
  if (minutesRaw !== undefined && minutesRaw !== '') {
    const m = Number(minutesRaw);
    if (!Number.isNaN(m) && m > 0) return m * 60 * 1000;
  }
  if (msRaw !== undefined && msRaw !== '') {
    const ms = Number(msRaw);
    if (!Number.isNaN(ms) && ms > 0) return ms;
  }
  return DEFAULT_IDLE_MINUTES * 60 * 1000;
}

/**
 * Logs out when the user has been idle (no input / no debounced API activity) for the configured period.
 */
export default function IdleSessionWatcher() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const lastActivityRef = useRef(Date.now());
  const mouseMoveAtRef = useRef(0);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const markActive = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const performIdleLogout = useCallback(() => {
    logoutRef.current();
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const idleMs = resolveIdleMs();
    lastActivityRef.current = Date.now();

    const onMouseMove = () => {
      const now = Date.now();
      if (now - mouseMoveAtRef.current < MOUSE_MOVE_THROTTLE_MS) return;
      mouseMoveAtRef.current = now;
      markActive();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityRef.current >= idleMs) {
        performIdleLogout();
      }
    };

    const onSessionActivityEvent = () => {
      markActive();
    };

    const domEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'];
    domEvents.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener(SESSION_ACTIVITY_EVENT, onSessionActivityEvent);

    const tick = () => {
      if (Date.now() - lastActivityRef.current >= idleMs) {
        performIdleLogout();
      }
    };
    const intervalId = window.setInterval(tick, CHECK_INTERVAL_MS);

    return () => {
      domEvents.forEach((evt) => window.removeEventListener(evt, markActive));
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener(SESSION_ACTIVITY_EVENT, onSessionActivityEvent);
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, markActive, performIdleLogout]);

  return null;
}
